import moment from 'moment'
import * as React from 'react'

import ClassroomStudentSection from './classroom_student_section'
import ClassroomTeacherSection from './classroom_teacher_section'
import { gradeText } from './gradeText'
import LeaveClassModal from './leave_class_modal'
import MergedClassesSection from './merged_classes_section'

import { Tooltip, helpIcon, } from '../../../Shared/index'
const expandSrc = `${window.postbuildEnv.CDN_URL}/images/icons/expand.svg`

export const leaveClassModal = 'leaveClassModal'

interface ClassroomProps {
  user: any;
  classroom: any;
  classrooms: Array<any>;
  selected: boolean;
  isOwnedByCurrentUser: boolean;
  clickClassroomHeader: (event) => void;
  renameClass?: (event) => void;
  changeGrade?: (event) => void;
  archiveClass?: (event) => void;
  unarchiveClass?: (event) => void;
  mergeClasses?: (event) => void;
  inviteStudents?: (event) => void;
  importProviderClassroomStudents?: (event) => void;
  importMergedSecondaryStudents?: (secondaryClassroom) => void;
  viewAsStudent?: (event) => void;
  onSuccess: (event) => void;
}

interface ClassroomState {
  showModal?: string;
}

export default class Classroom extends React.Component<ClassroomProps, ClassroomState> {
  constructor(props) {
    super(props)

    this.state = {
      showModal: null
    }

    this.leaveClass = this.leaveClass.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.renderLeaveClassModal = this.renderLeaveClassModal.bind(this)
  }

  leaveClass() {
    this.setState({ showModal: leaveClassModal })
  }

  closeModal() {
    this.setState({ showModal: null })
  }

  renderClassCode() {
    const { classroom } = this.props
    const { code, classroomProvider } = classroom

    if (classroomProvider) {
      return (
        <Tooltip
          tooltipText={`Class code: <b>${code}</b><br/><br/>The easiest way for your students to join your class is through ${classroomProvider}. However, if your students are not syncing, try the class code.`}
          tooltipTriggerText={<div className="text-and-icon-wrapper"><span>Class code:&nbsp;</span><img alt={helpIcon.alt} src={helpIcon.src} /></div>}
        />
      )
    }

    return `Class code: ${code}`
  }

  renderGrade() {
    const { classroom } = this.props
    return gradeText(classroom.grade)
  }

  renderClassType() {
    const { classroom } = this.props
    const { classroom_external_id } = classroom

    if (!classroom_external_id) { return null }

    return (
      <React.Fragment key={`class-type-item-${classroom.id}`}>
        <span className="item">{this.renderClassTypeContent()}</span>
        <span className="bullet item">•</span>
      </React.Fragment>
    )
  }

  renderClassTypeContent() {
    const { classroom } = this.props
    const { name, synced_name } = classroom

    const text = classroom.classroomProvider

    if (synced_name === null || synced_name === name) {  return text }

    return (
      <Tooltip
        tooltipText={`Source: ${synced_name}`}
        tooltipTriggerText={
          <div className="text-and-icon-wrapper">
            <span>{text}&nbsp;</span>
            <img
              alt={helpIcon.alt}
              src={helpIcon.src}
            />
          </div>
        }
      />
    )
  }

  renderArchivedDate() {
    const { classroom } = this.props
    const updatedAt = moment(classroom.updated_at).format('MMM D, YYYY')

    if (classroom.visible) { return null}

    return (
      <React.Fragment key={`archived-at-bullet-${classroom.id}`}>
        <span className="bullet item">•</span>
        <span className="item">Archived {updatedAt}</span>
      </React.Fragment>
    )
  }

  renderCoteachers() {
    const { classroom } = this.props
    const numberOfStudents = classroom.students.length
    const numberOfTeachers = classroom.teachers.length

    if (numberOfStudents <= 1) { return null }

    return (
      <React.Fragment key={`number-of-coteachers-${classroom.id}`}>
        <span className="item">
          {numberOfTeachers - 1} {numberOfTeachers === 2 ? 'co-teacher' : 'co-teachers'}
        </span>
        <span className="bullet item">•</span>
      </React.Fragment>
    )
  }

  renderClassroomData() {
    const { classroom } = this.props
    const numberOfStudents = classroom.students.length
    const createdAt = moment(classroom.created_at).format('MMM D, YYYY')

    return (
      <div className="classroom-data">
        <span className="item">{numberOfStudents} {numberOfStudents === 1 ? 'student' : 'students'}</span>
        <span className="bullet item">•</span>
        {this.renderCoteachers()}
        {this.renderClassType()}
        <span className="item">{this.renderClassCode()}</span>
        <span className="bullet item">•</span>
        <span className="item">{this.renderGrade()}</span>
        <span className="bullet item">•</span>
        <span className="item">Created {createdAt}</span>
        {this.renderArchivedDate()}
      </div>
    )
  }

  renderClassroomHeader() {
    const { classroom, clickClassroomHeader, selected, } = this.props

    return (
      <div className="classroom-card-header" onClick={() => clickClassroomHeader(classroom.id)}>
        <div className="classroom-info">
          <h2 className="classroom-name" id={`classroom${classroom.id}Title`}>{classroom.name}</h2>
          {this.renderClassroomData()}
        </div>
        <button aria-expanded={selected} aria-label={selected ? 'Collapse classroom' : 'Expand classroom'} className="interactive-wrapper focus-on-light" onClick={() => clickClassroomHeader(classroom.id)} type="button">
          <img alt="" className="expand-arrow" src={expandSrc} />
        </button>
      </div>
    )
  }

  renderClassSettings() {
    const {
      isOwnedByCurrentUser,
      classroom,
      renameClass,
      changeGrade,
      archiveClass,
      unarchiveClass,
      mergeClasses,
    } = this.props
    let coteacherNote
    let classSettingsClassName = "class-settings"
    let settings = [
      <button className="quill-button outlined small grey focus-on-light" key={`rename-class-${classroom.id}`} onClick={renameClass} type="button">Rename class</button>,
      <button className="quill-button outlined small grey focus-on-light" key={`change-grade-${classroom.id}`} onClick={changeGrade} type="button">Change grade</button>,
      <button className="quill-button outlined small grey focus-on-light" key={`archive-class-${classroom.id}`} onClick={archiveClass} type="button">Archive</button>
    ]

    if (mergeClasses) {
      settings.push(<button className="quill-button outlined small grey focus-on-light" key={`merge-classes-${classroom.id}`} onClick={mergeClasses} type="button">Merge classes</button>)
    }

    if (!isOwnedByCurrentUser) {
      const owner = classroom.teachers.find(t => t.classroom_relation === 'owner')
      coteacherNote = <p className="coteacher-note">Looking for more class settings? Ask {owner.name}, the class owner.</p>
      settings = [<button className="quill-button outlined small green focus-on-light" key={`leave-class-${classroom.id}`} onClick={this.leaveClass} type="button">Leave class</button>]
      classSettingsClassName+= ' coteacher-class-settings'
    } else if (!classroom.visible) {
      // a merged class cannot be unarchived until it is restored from its primary class, so we hide the button
      settings = classroom.classroom_merge ? [] : [
        <button className="quill-button outlined small green focus-on-light" key={`unarchive-class-${classroom.id}`} onClick={unarchiveClass} type="button">Un-archive</button>
      ]
    }

    if (!settings.length && !coteacherNote) { return null }

    return (
      <div className={classSettingsClassName}>
        <h3>Class settings</h3>
        {coteacherNote}
        <div className="class-settings-buttons">
          {settings}
        </div>
      </div>
    )
  }

  renderClassroomContent() {
    const {
      user,
      classroom,
      onSuccess,
      inviteStudents,
      classrooms,
      isOwnedByCurrentUser,
      importProviderClassroomStudents,
      importMergedSecondaryStudents,
      viewAsStudent,
    } = this.props
    const sharedProps = {
      user,
      classroom,
      onSuccess,
      isOwnedByCurrentUser,
      classrooms
    }
    return (
      <div>
        {this.renderClassSettings()}
        <MergedClassesSection
          allowRestore={!!classroom.visible && isOwnedByCurrentUser}
          classroom={classroom}
          importMergedSecondaryStudents={importMergedSecondaryStudents}
          onSuccess={onSuccess}
          user={user}
        />
        <ClassroomStudentSection
          {...sharedProps}
          importProviderClassroomStudents={importProviderClassroomStudents}
          inviteStudents={inviteStudents}
          viewAsStudent={viewAsStudent}
        />
        <ClassroomTeacherSection
          {...sharedProps}
          leaveClass={this.leaveClass}
        />
      </div>
    )
  }

  renderLeaveClassModal() {
    const { classroom, onSuccess, } = this.props
    const { showModal } = this.state
    if (showModal === leaveClassModal) {
      return (
        <LeaveClassModal
          classroom={classroom}
          close={this.closeModal}
          onSuccess={onSuccess}
        />
      )
    }
  }

  renderClassroom() {
    const { selected, classroom, } = this.props
    return (
      <section aria-labelledby={`classroom${classroom.id}Title`} className={`classroom ${selected ? 'open' : 'closed'}`} data-testid={classroom.visible ? 'visible-classroom' : 'archived-classroom'} id={classroom.id}>
        {this.renderLeaveClassModal()}
        {this.renderClassroomHeader()}
        {selected ? this.renderClassroomContent() : null}
      </section>
    )
  }

  render() {
    return this.renderClassroom()
  }
}
